import React from "react";
import clsx from "clsx";
import { fileNameFromUrl, isFirstPartyArtifactUrl, upgradeInsecureUrl } from "@/utils/artifactUrl";
import { getSafeHref, isUrlSafe } from "@/utils/common/security";
import { BaseButtonType } from "@/components/library/BaseButton";
import Button from "@/components/library/Button";
import Icon, { IconNameExtended } from "@/components/library/Icon";

export default function LinkRenderer(
   props: any,
   sendAction?: (title: string, action: string | null) => Promise<void>,
   triggerDownload?: (url: string, fileName: string) => void
) {
   const children = props.children;
   const isAction = typeof children === "string" && children.startsWith("$$");

   if (isAction && sendAction) {
      const params = children.replace("$$", "").split(";");
      const icon = params.find((param: string) => param.startsWith("i:"));
      const title = params.find((param: string) => param.startsWith("t:"));
      const description = params.find((param: string) => param.startsWith("d:"));
      const action = params.find((param: string) => param.startsWith("a:"));
      const type = params.find((param: string) => param.startsWith("b:"));

      const iconName = icon ? icon.split(":")[1] : null;
      const titleName = title ? title.split(":")[1] : null;
      const actionName = action ? action.split(":")[1] : null;
      const descriptionName = description ? description.split(":")[1] : null;
      const typeName = type ? type.split(":")[1] : "tertiary";

      return (
         <Button
            type={typeName as BaseButtonType}
            onClick={() => sendAction(titleName ?? "", actionName)}
            disabled={!actionName}
         >
            {iconName && !titleName && (
               <Icon icon={iconName as IconNameExtended} size={3} className="m-auto" />
            )}
            {iconName && titleName && (
               <div className={clsx("grid", "grid-cols-[60px_1fr]", "text-left")}>
                  <Icon icon={iconName as IconNameExtended} size={3} className="m-auto" />
                  <div>
                     <div className="font-semibold">{titleName}</div>
                     {descriptionName && <span className="text-sm">{descriptionName}</span>}
                  </div>
               </div>
            )}
            {!iconName && titleName && (
               <>
                  <div className="font-semibold">{titleName}</div>
                  {descriptionName && <span className="text-sm">{descriptionName}</span>}
               </>
            )}
         </Button>
      );
   }

   if (!isUrlSafe(props.href)) {
      return <span>{children}</span>;
   }

   // Normalize once: getSafeHref trims whitespace and returns "#" for unsafe
   // input, so downstream checks (host match, http→https upgrade) operate on
   // a canonical string instead of the raw href.
   const safeHref = upgradeInsecureUrl(getSafeHref(props.href));

   // Route first-party artifact links (host matches NEXT_PUBLIC_WINGMAN_URL)
   // through the blob downloader so the actual download is served from a
   // same-origin object URL with an octet-stream MIME type.
   if (triggerDownload && isFirstPartyArtifactUrl(safeHref)) {
      const fileName = fileNameFromUrl(safeHref);
      return (
         <a
            href={safeHref}
            // @bprogress listens for anchor clicks in the capture phase, so it
            // runs before React's onClick preventDefault and would start the
            // top progress bar for this non-navigating download click — and
            // never stop it. data-prevent-progress is its opt-out.
            data-prevent-progress="true"
            onClick={(e) => {
               if (e.defaultPrevented) return;
               if (e.button !== 0) return;
               if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
               e.preventDefault();
               triggerDownload(safeHref, fileName);
            }}
         >
            {children}
         </a>
      );
   }

   return (
      <a href={safeHref} target="_blank" rel="noopener noreferrer">
         {children}
      </a>
   );
}
